'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SectionHeading } from '@/components/shared/SectionHeading';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { AmbassadorContent } from '@/lib/content-schema';

interface Props {
  content: AmbassadorContent['valueTable'];
}

export function ValueTable({ content }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="ventajas" className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading title={content.heading} />

        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
        >
          <div className="hidden md:block">
            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-zibarit-verde-oscuro text-zibarit-bone rounded-xl px-6 py-4 text-center">
                <span className="text-subtitle text-sm uppercase tracking-wider">
                  {content.columns[0]}
                </span>
              </div>
              <div className="bg-zibarit-verde-brillante text-zibarit-negro rounded-xl px-6 py-4 text-center">
                <span className="text-subtitle text-sm uppercase tracking-wider">
                  {content.columns[1]}
                </span>
              </div>
            </motion.div>

            {content.rows.map((row, i) => (
              <motion.div key={i} variants={fadeInUp} className="grid grid-cols-2 gap-4 mb-3">
                <div className="bg-zibarit-bone rounded-xl px-6 py-4">
                  <p className="text-body text-sm text-zibarit-marron-oscuro">{row[0]}</p>
                </div>
                <div className="bg-zibarit-verde-brillante/10 rounded-xl px-6 py-4">
                  <p className="text-body text-sm text-zibarit-negro font-medium">{row[1]}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="md:hidden space-y-4">
            {content.rows.map((row, i) => (
              <motion.div key={i} variants={fadeInUp} className="bg-zibarit-bone rounded-xl p-5 space-y-3">
                <div>
                  <span className="text-subtitle text-xs uppercase tracking-wider text-zibarit-verde-oscuro">
                    {content.columns[0]}
                  </span>
                  <p className="text-body text-sm text-zibarit-marron-oscuro mt-1">{row[0]}</p>
                </div>
                <div className="border-t border-zibarit-marron-secundario/20 pt-3">
                  <span className="text-subtitle text-xs uppercase tracking-wider text-zibarit-verde-brillante">
                    {content.columns[1]}
                  </span>
                  <p className="text-body text-sm text-zibarit-negro font-medium mt-1">{row[1]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
